import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Content from '../../layout/content/Content'
import Head from '../../layout/head/Head'
import { PersonnelServices } from '../../services/McDataServices'
import { PersonnelDataProcessor } from '../mc-components/McDataProcessor'
import { useGlobalStoreData } from '../../store/globalStore'
import { Col, Label, Row, Spinner } from 'reactstrap'
import { MarketCalculusCard } from '../../components/preview/Preview'
import McPageHelpers from '../mc-components/McPageHelpers'
import MCTitleCard from '../../components/title-card/MCTitleCard'
import CONSTANTS from '../../utils/Constants'
import PersonnelClusteredBarChart from '../../components/amcharts/PersonnelClusteredBarChart'
import StackedBarChart from '../../components/amcharts/StackedBarChart'
import PersonnelMarketShareTable from '../../components/table/PersonnelMarketShareTable'
import {
  useSDSfirstMenuItemLink, useSDSimaginalUrlSearchParams,
  useSDSselectBoxData
} from '../../store/searchDataStore'
import { RSelect } from '../../components/Component'
import { DatePicker, ConfigProvider } from 'antd'
import locale from 'antd/locale/tr_TR'
import enLocale from 'antd/locale/en_US'
import dayjs from 'dayjs'
import 'dayjs/locale/tr'
import 'dayjs/locale/en'

const PersonnelMarketChanges = ({ userRoles }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [importerCountryCode, setImporterCountryCode] = useState('')
  const [exporterCountryCode, setExporterCountryCode] = useState('')
  const [hsCodes, setHsCodes] = useState([])
  const [firstPeriod, setFirstPeriod] = useState(dayjs('2023-01'))
  const [secondPeriod, setSecondPeriod] = useState(dayjs('2024-01'))
  const [periodInMonths, setPeriodInMonths] = useState(6)
  const [loading, setLoading] = useState(false)

  const countriesGSD = useGlobalStoreData.countries()
  const [rawPersonnelMarketChanges, setRawPersonnelMarketChanges] = useState()
  const [personnelMarketChanges, setPersonnelMarketChanges] = useState({
    clustered: [],
    stacked: [],
    table: [],
    marketShareChangeExplanation: '',
    marketShareChangeDetailsExplanation: ''
  })

  const header = t('market_changes_table_title')
  const subHeader = ''// importerCountryName + ' ' + searchParams.hsCode

  const handleSearch = () => {
    setLoading(true)

    const payload = {
      importerCountryCode,
      exporterCountryCode,
      hsCodes,
      firstDate: new Date(secondPeriod.format('YYYY-MM-01')).toISOString(),
      secondDate: new Date(firstPeriod.format('YYYY-MM-01')).toISOString(),
      periodInMonths
    }

    PersonnelServices.MarketChanges(payload).then(data => {
      const clustered = PersonnelDataProcessor.barChart(data, countriesGSD)
      const stacked = PersonnelDataProcessor.stackedChart(data?.data,
        countriesGSD)
      const table = data?.data?.reverse()

      const enrichedData = { ...data, periodInMonths }

      setRawPersonnelMarketChanges(enrichedData)
      setPersonnelMarketChanges({
        clustered,
        stacked,
        table,
        marketShareChangeExplanation: data.marketShareChangeExplanation,
        marketShareChangeDetailsExplanation: data.marketShareChangeDetailsExplanation
      })
    }).finally(() => setLoading(false))
  }
  const selectBoxData = useSDSselectBoxData()
  const [showImporterOpts, setShowImporterOpts] = useState(false)
  const [showExporterOpts, setShowExporterOpts] = useState(false)
  const [showHsOpts, setShowHsOpts] = useState(false)

  const languageGSD = useGlobalStoreData.language()
  const firstMenuItemLink = useSDSfirstMenuItemLink()
  const urlSearchParams = useSDSimaginalUrlSearchParams()

  const antdLocale = languageGSD === 'tr' ? locale : enLocale
  dayjs.locale(languageGSD === 'tr' ? 'tr' : 'en')

  const handleRestrictedAccess = () => {
    if (!userRoles.PERSONNEL) {
      navigate(
        `${process.env.PUBLIC_URL}/${languageGSD}${firstMenuItemLink}${urlSearchParams
          ? '?' + urlSearchParams
          : ''}`)
    }
  }
  useEffect(handleRestrictedAccess, [userRoles.PERSONNEL])

  return (
    <>
      <Head title={t('menu.personnelMarketChanges')}/>
      <Content>
        <MarketCalculusCard className="mb-5">
          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="form-group">
                <Label htmlFor="hsCodes" className="form-label mb-0">
                  {t('personnel.marketChanges.hsCodes')}
                </Label>
                <div className="form-control-wrap">
                  <RSelect
                    id="hsCodes"
                    options={showHsOpts
                      ? selectBoxData.hsCode.filter(
                        x => !hsCodes.includes(x.value))
                      : []}
                    isLoading={!selectBoxData.hsCode?.length}
                    isMulti
                    placeholder={t('personnel.marketChanges.hsCodes')}
                    value={selectBoxData.hsCode.filter(
                      x => hsCodes.includes(x.value))}
                    onInputChange={val => setShowHsOpts(val.length >= 2)}
                    onChange={selected => setHsCodes(
                      selected.map(opt => opt.value))}
                    formatOptionLabel={opt =>
                      <small>{opt.value}</small>}
                    noOptionsMessage={() => showHsOpts
                      ? t('no_option')
                      : t(
                        'min_3_char')}
                  />
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 120 }}>
              <div className="form-group">
                <Label htmlFor="importerCountry" className="form-label mb-0">
                  {t('personnel.marketChanges.importerCountry')}
                </Label>
                <div className="form-control-wrap">
                  <RSelect
                    id="importerCountry"
                    options={showImporterOpts ? selectBoxData.coverageData : []}
                    isLoading={!selectBoxData.coverageData?.length}
                    placeholder={t('personnel.marketChanges.importerCountry')}
                    value={selectBoxData.coverageData?.find(
                      c => c.countryCode === importerCountryCode) || null}
                    onInputChange={val => setShowImporterOpts(val.length >= 2)}
                    onChange={opt => setImporterCountryCode(opt.countryCode)}
                    filterOption={(
                      opt,
                      input) => `${opt.data.label} ${opt.data.abbreviation ||
                    ''}`.toLowerCase().includes(input.toLowerCase())}
                  />
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 120 }}>
              <div className="form-group">
                <Label htmlFor="exporterCountry" className="form-label mb-0">
                  {t('personnel.marketChanges.exporterCountry')}
                </Label>
                <div className="form-control-wrap">
                  <RSelect
                    id="exporterCountry"
                    options={showExporterOpts ? selectBoxData.countries : []}
                    isLoading={!selectBoxData.countries?.length}
                    placeholder={t('personnel.marketChanges.exporterCountry')}
                    value={selectBoxData.countries?.find(
                      c => c.countryCode === exporterCountryCode) || null}
                    onInputChange={val => setShowExporterOpts(val.length >= 2)}
                    onChange={opt => setExporterCountryCode(opt.countryCode)}
                    filterOption={(
                      opt,
                      input) => `${opt.data.label} ${opt.data.abbreviation ||
                    ''}`.toLowerCase().includes(input.toLowerCase())}
                  />
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 150 }}>
              <div className="form-group">
                <Label htmlFor="firstPeriod" className="form-label mb-0">
                  {t('personnel.marketChanges.firstPeriod')}
                </Label>
                <div className="form-control-wrap">
                  <ConfigProvider locale={antdLocale}>
                    <DatePicker
                      value={firstPeriod}
                      onChange={(date) => setFirstPeriod(date)}
                      picker="month"
                      format="MM/YYYY"
                      style={{ width: '100%' }}
                      size="middle"
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 150 }}>
              <div className="form-group">
                <Label htmlFor="secondPeriod" className="form-label mb-0">
                  {t('personnel.marketChanges.secondPeriod')}
                </Label>
                <div className="form-control-wrap">
                  <ConfigProvider locale={antdLocale}>
                    <DatePicker
                      value={secondPeriod}
                      onChange={(date) => setSecondPeriod(date)}
                      picker="month"
                      format="MM/YYYY"
                      style={{ width: '100%' }}
                      size="middle"
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>

            <div style={{ width: 120 }}>
              <div className="form-group">
                <Label htmlFor="periodInMonths" className="form-label mb-0">
                  {t('personnel.marketChanges.periodInMonths')}
                </Label>
                <div className="form-control-wrap">
                  <input
                    id="periodInMonths"
                    type="number"
                    className="form-control"
                    value={periodInMonths}
                    onChange={e => setPeriodInMonths(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div style={{ alignSelf: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSearch}
                      disabled={loading}>
                {loading
                  ? <Spinner size="sm" color="light"/>
                  : t(
                    'common.search')}
              </button>
            </div>
          </div>
        </MarketCalculusCard>

        <Row className="my-3">
          {!!personnelMarketChanges.table?.length &&
            <Col md="12">
              <MarketCalculusCard
                id={'marketShareTable' + McPageHelpers.FOR_PDF_CONTENT_ID}
                className="mb-4">
                <MCTitleCard code={CONSTANTS.BADGE_CODES.MARKET_CHANGE_TABLE_1}
                             chartSubTitle={subHeader} chartTitle={header}
                             codeColor="info"
                />
                <PersonnelMarketShareTable data={personnelMarketChanges.table}
                                  responseCountry={countriesGSD}
                                  yearlyHsSums={rawPersonnelMarketChanges?.yearlyHsSums}
                                  periodInMonths={rawPersonnelMarketChanges?.periodInMonths}
                                  footer={personnelMarketChanges.marketShareChangeDetailsExplanation}
                                />
              </MarketCalculusCard>
            </Col>}
          {!!personnelMarketChanges?.clustered?.stackedData?.length &&
            <Col md="6">
              <MarketCalculusCard
                id={'clusteredBarChart' + McPageHelpers.FOR_PDF_CONTENT_ID}
                className={'h-100'}>
                <div id={'clusteredBarChart' + McPageHelpers.FOR_PDF_ID}
                     className="h-100">
                  <MCTitleCard
                    code={CONSTANTS.BADGE_CODES.MARKET_CHANGE_CLUSTERED}
                    chartSubTitle={t('country_code_subtitle', {
                      country: countriesGSD.find(c => c.countryCode === importerCountryCode)?.countryName || importerCountryCode,
                      hsCode: hsCodes.join(', ')
                    })}
                    chartTitle={t('clustered_bar_chart_change_of_rates_title')}/>
                  <div className={'h-100'}>
                    <PersonnelClusteredBarChart
                      response={personnelMarketChanges?.clustered}
                      xTitle={t('sp_percentage')}
                      yTitle={t('clustered_bar_chart_yTitle')}
                      yearlyHsSums={rawPersonnelMarketChanges?.yearlyHsSums}
                      periodInMonths={rawPersonnelMarketChanges?.periodInMonths}
                      footer={personnelMarketChanges.marketShareChangeExplanation}
                    />
                  </div>
                </div>

                {McPageHelpers.generateExplanationFooter(
                  personnelMarketChanges.marketShareChangeExplanation)}
              </MarketCalculusCard>
            </Col>}
          {!!personnelMarketChanges?.stacked?.stackedData?.length &&
            <Col md="6">
              <MarketCalculusCard
                id={'stackedBarChart' + McPageHelpers.FOR_PDF_CONTENT_ID}
                className={'h-100'}>
                <div id={'stackedBarChart' + McPageHelpers.FOR_PDF_ID}
                     className="h-100">
                  <MCTitleCard
                    code={CONSTANTS.BADGE_CODES.MARKET_CHANGE_STACKED}
                    chartSubTitle={t('country_code_subtitle', {
                      country: countriesGSD.find(c => c.countryCode === importerCountryCode)?.countryName || importerCountryCode,
                      hsCode: hsCodes.join(', ')
                    })} chartTitle={t('stacked_bar_chart_change_of_rates_title')}
                  />
                  <div

                    className={'h-100'}>
                    <StackedBarChart response={personnelMarketChanges.stacked}
                                     xTitle={t('stacked_bar_chart_xTitle')}
                                     yTitle={t('clustered_bar_chart_yTitle')}
                                     country={countriesGSD}
                    />
                  </div>
                </div>

              </MarketCalculusCard>
            </Col>
          }
        </Row>

      </Content>
    </>
  )
}

export default PersonnelMarketChanges
